// The privacy policy of the app (not of the marketing site). Every statement
// here describes what the platform actually does - keep it in step with the
// code: the sign-in cookie (ADR-0019), hand-over provenance (ADR-0020), the
// activity stamp (ADR-0030), the services the backend calls. The Google
// section is what Google's OAuth verification of the Calendar scope requires.

export type PrivacySection = {
  title: string
  paragraphs?: string[]
  items?: string[]
  /** Followed by the links to Google's policy and account permissions. */
  googleLinks?: boolean
}

export type PrivacyContent = {
  title: string
  updated: string
  intro: string
  sections: PrivacySection[]
  googlePolicyLink: string
  googlePermissionsLink: string
  contactTitle: string
  contactCompany: string
  contactEmail: string
  contactWebsite: string
  back: string
}

export const PRIVACY_UPDATED_ISO = '2026-09-22'

const en: PrivacyContent = {
  title: 'Privacy policy',
  updated: 'Last updated: 22 September 2026',
  intro:
    'MielFlow is a platform businesses use to run their orders, production, stock, quotes and invoices. This policy explains which personal data the platform processes, why, and what you can do about it.',
  sections: [
    {
      title: '1. Who is responsible',
      paragraphs: [
        'The business that gave you your account (your supplier or your employer) decides which orders, customers and documents are recorded and is responsible for that data. MielFlow runs the platform on its behalf and processes the data only to provide the service.',
        'Questions about the data a business keeps about you are best sent to that business. You can always contact MielFlow as well, using the details at the end of this page.',
      ],
    },
    {
      title: '2. What we process',
      items: [
        'Account details: name, e-mail address, phone number, language, role and, for dealers, company, address and financial details (VAT and chamber of commerce numbers, IBAN).',
        'Business records: orders, quotes, invoices, payments, customers and the people named on them.',
        'Hand-over records: photos of delivered goods, the customer\'s signature, and the time, IP address and browser of the device used to sign.',
        'Support tickets and comments, including screenshots you attach.',
        'Usage data: when you last signed in and were last active, and a history of changes (who changed what, and when).',
        'Messages: the e-mails and in-app notifications the platform sends you, and whether they were delivered.',
        'Leads from advertising forms, if the business connects them to the platform.',
      ],
    },
    {
      title: '3. Why we process it',
      items: [
        'To provide the service you or your business signed up for: taking and producing orders, delivering, invoicing and support (performance of a contract).',
        'To meet legal obligations, such as keeping invoices for as long as tax law requires.',
        'To keep the platform secure and reliable, for example by recording who changed what and who signed for a delivery (legitimate interest).',
      ],
      paragraphs: ['We do not sell personal data, use it for advertising or build profiles of users.'],
    },
    {
      title: '4. Cookies and browser storage',
      paragraphs: [
        'The platform sets one cookie, sm_refresh, which keeps you signed in. It is strictly necessary, cannot be read by scripts, and ends 30 days after you last used the platform (90 days at most). Signing out removes it.',
        'Your browser also stores your sign-in token, your language and your display preferences. There are no analytics, advertising or tracking cookies.',
      ],
    },
    {
      title: '5. Services we use',
      items: [
        'Google Cloud: hosting, database and file storage, in the European Union.',
        'The e-mail server configured by your business, to send notifications and documents.',
        'Mollie, when you pay online. Card and bank details go to Mollie directly; the platform does not store them.',
        'Google Calendar, only if you connect it (see below).',
        'Meta (Facebook) Lead Ads, only if your business connects its advertising forms.',
      ],
    },
    {
      title: '6. Google user data',
      googleLinks: true,
      paragraphs: [
        'If you connect a Google account, the platform reads your basic profile (name and e-mail address) and, with your explicit permission, your Google Calendar events, to show and plan appointments such as deliveries. This data is used for that feature only and is never sold or used for advertising.',
        'Our use of information received from Google APIs adheres to the Google API Services User Data Policy, including the Limited Use requirements. You can revoke access at any time in your Google account.',
      ],
    },
    {
      title: '7. How long we keep it',
      paragraphs: [
        'Data is kept for as long as your account or your business relationship requires it. Invoices and the records behind them are kept for as long as tax law requires (seven years in the Netherlands). Sign-in sessions expire as described above. When a business stops using the platform, its data is deleted after the agreed period.',
      ],
    },
    {
      title: '8. Security',
      paragraphs: [
        'All connections are encrypted (HTTPS). Access is limited by role, so every user sees only what their role allows, and changes are recorded. Sign-in sessions are stored only as one-way hashes and end when you sign out or your password changes.',
      ],
    },
    {
      title: '9. Your rights',
      paragraphs: [
        'You have the right to access, correct or delete your personal data, to restrict or object to its processing, and to receive it in a portable form. You can also lodge a complaint with your data protection authority (in the Netherlands: the Autoriteit Persoonsgegevens).',
      ],
    },
    {
      title: '10. Changes to this policy',
      paragraphs: [
        'We may update this policy when the platform changes. The date at the top shows the latest version.',
      ],
    },
  ],
  googlePolicyLink: 'Google API Services User Data Policy',
  googlePermissionsLink: 'Manage Google account access',
  contactTitle: '11. Contact',
  contactCompany: 'Company',
  contactEmail: 'E-mail',
  contactWebsite: 'Website',
  back: 'Back',
}

const nl: PrivacyContent = {
  title: 'Privacybeleid',
  updated: 'Laatst bijgewerkt: 22 september 2026',
  intro:
    'MielFlow is een platform waarmee bedrijven hun bestellingen, productie, voorraad, offertes en facturen beheren. Dit beleid legt uit welke persoonsgegevens het platform verwerkt, waarom, en wat u daarmee kunt doen.',
  sections: [
    {
      title: '1. Wie is verantwoordelijk',
      paragraphs: [
        'Het bedrijf dat u uw account gaf (uw leverancier of uw werkgever) bepaalt welke bestellingen, klanten en documenten worden vastgelegd en is verantwoordelijk voor die gegevens. MielFlow beheert het platform namens dat bedrijf en verwerkt de gegevens alleen om de dienst te leveren.',
        'Vragen over de gegevens die een bedrijf over u bewaart, stelt u het best aan dat bedrijf. U kunt ook altijd contact opnemen met MielFlow via de gegevens onderaan deze pagina.',
      ],
    },
    {
      title: '2. Welke gegevens we verwerken',
      items: [
        'Accountgegevens: naam, e-mailadres, telefoonnummer, taal, rol en, voor dealers, bedrijf, adres en financiële gegevens (btw- en KvK-nummer, IBAN).',
        'Bedrijfsgegevens: bestellingen, offertes, facturen, betalingen, klanten en de personen die daarop staan.',
        'Overdrachtsgegevens: foto\'s van geleverde goederen, de handtekening van de klant, en het tijdstip, IP-adres en de browser van het apparaat waarmee is getekend.',
        'Supporttickets en reacties, inclusief schermafbeeldingen die u bijvoegt.',
        'Gebruiksgegevens: wanneer u voor het laatst inlogde en actief was, en een geschiedenis van wijzigingen (wie wat wanneer wijzigde).',
        'Berichten: de e-mails en meldingen die het platform u stuurt, en of ze zijn afgeleverd.',
        'Leads uit advertentieformulieren, als het bedrijf die aan het platform koppelt.',
      ],
    },
    {
      title: '3. Waarom we ze verwerken',
      items: [
        'Om de dienst te leveren waarvoor u of uw bedrijf zich aanmeldde: bestellingen aannemen en produceren, leveren, factureren en ondersteunen (uitvoering van een overeenkomst).',
        'Om aan wettelijke verplichtingen te voldoen, zoals het bewaren van facturen zolang de belastingwet dat vereist.',
        'Om het platform veilig en betrouwbaar te houden, bijvoorbeeld door vast te leggen wie wat wijzigde en wie voor een levering tekende (gerechtvaardigd belang).',
      ],
      paragraphs: ['We verkopen geen persoonsgegevens, gebruiken ze niet voor advertenties en maken geen profielen van gebruikers.'],
    },
    {
      title: '4. Cookies en browseropslag',
      paragraphs: [
        'Het platform plaatst één cookie, sm_refresh, dat u ingelogd houdt. Het is strikt noodzakelijk, kan niet door scripts worden gelezen en vervalt 30 dagen nadat u het platform voor het laatst gebruikte (hooguit 90 dagen). Uitloggen verwijdert het.',
        'Uw browser bewaart daarnaast uw inlogtoken, uw taal en uw weergave-instellingen. Er zijn geen analytische, advertentie- of trackingcookies.',
      ],
    },
    {
      title: '5. Diensten die we gebruiken',
      items: [
        'Google Cloud: hosting, database en bestandsopslag, in de Europese Unie.',
        'De e-mailserver die uw bedrijf heeft ingesteld, om meldingen en documenten te versturen.',
        'Mollie, als u online betaalt. Kaart- en bankgegevens gaan rechtstreeks naar Mollie; het platform bewaart ze niet.',
        'Google Agenda, alleen als u die koppelt (zie hieronder).',
        'Meta (Facebook) Lead Ads, alleen als uw bedrijf zijn advertentieformulieren koppelt.',
      ],
    },
    {
      title: '6. Google-gebruikersgegevens',
      googleLinks: true,
      paragraphs: [
        'Als u een Google-account koppelt, leest het platform uw basisprofiel (naam en e-mailadres) en, met uw uitdrukkelijke toestemming, uw Google Agenda-afspraken, om afspraken zoals leveringen te tonen en te plannen. Deze gegevens worden alleen voor die functie gebruikt en nooit verkocht of voor advertenties gebruikt.',
        'Ons gebruik van informatie uit Google API\'s volgt het Google API Services User Data Policy, inclusief de Limited Use-vereisten. U kunt de toegang op elk moment intrekken in uw Google-account.',
      ],
    },
    {
      title: '7. Hoe lang we ze bewaren',
      paragraphs: [
        'Gegevens worden bewaard zolang uw account of uw zakelijke relatie dat vereist. Facturen en de gegevens erachter worden bewaard zolang de belastingwet dat vereist (in Nederland zeven jaar). Inlogsessies vervallen zoals hierboven beschreven. Als een bedrijf stopt met het platform, worden zijn gegevens na de afgesproken termijn verwijderd.',
      ],
    },
    {
      title: '8. Beveiliging',
      paragraphs: [
        'Alle verbindingen zijn versleuteld (HTTPS). Toegang is per rol beperkt, zodat iedere gebruiker alleen ziet wat zijn rol toestaat, en wijzigingen worden vastgelegd. Inlogsessies worden alleen als eenrichtings-hash opgeslagen en eindigen als u uitlogt of uw wachtwoord wijzigt.',
      ],
    },
    {
      title: '9. Uw rechten',
      paragraphs: [
        'U hebt het recht om uw persoonsgegevens in te zien, te corrigeren of te laten verwijderen, de verwerking te beperken of er bezwaar tegen te maken, en ze in een overdraagbare vorm te ontvangen. U kunt ook een klacht indienen bij de Autoriteit Persoonsgegevens.',
      ],
    },
    {
      title: '10. Wijzigingen in dit beleid',
      paragraphs: [
        'We kunnen dit beleid bijwerken als het platform verandert. De datum bovenaan toont de laatste versie.',
      ],
    },
  ],
  googlePolicyLink: 'Google API Services User Data Policy',
  googlePermissionsLink: 'Toegang tot uw Google-account beheren',
  contactTitle: '11. Contact',
  contactCompany: 'Bedrijf',
  contactEmail: 'E-mail',
  contactWebsite: 'Website',
  back: 'Terug',
}

const de: PrivacyContent = {
  title: 'Datenschutzerklärung',
  updated: 'Zuletzt aktualisiert: 22. September 2026',
  intro:
    'MielFlow ist eine Plattform, mit der Unternehmen ihre Bestellungen, Produktion, Lagerbestände, Angebote und Rechnungen verwalten. Diese Erklärung beschreibt, welche personenbezogenen Daten die Plattform verarbeitet, warum, und welche Rechte Sie haben.',
  sections: [
    {
      title: '1. Wer verantwortlich ist',
      paragraphs: [
        'Das Unternehmen, das Ihnen Ihr Konto gegeben hat (Ihr Lieferant oder Ihr Arbeitgeber), entscheidet, welche Bestellungen, Kunden und Dokumente erfasst werden, und ist für diese Daten verantwortlich. MielFlow betreibt die Plattform in dessen Auftrag und verarbeitet die Daten nur, um den Dienst zu erbringen.',
        'Fragen zu den Daten, die ein Unternehmen über Sie speichert, richten Sie am besten an dieses Unternehmen. Sie können sich aber auch jederzeit an MielFlow wenden; die Kontaktdaten finden Sie am Ende dieser Seite.',
      ],
    },
    {
      title: '2. Welche Daten wir verarbeiten',
      items: [
        'Kontodaten: Name, E-Mail-Adresse, Telefonnummer, Sprache, Rolle und bei Händlern Firma, Adresse und Finanzdaten (USt-IdNr., Handelsregisternummer, IBAN).',
        'Geschäftsdaten: Bestellungen, Angebote, Rechnungen, Zahlungen, Kunden und die darauf genannten Personen.',
        'Übergabedaten: Fotos gelieferter Waren, die Unterschrift des Kunden sowie Zeitpunkt, IP-Adresse und Browser des Geräts, mit dem unterschrieben wurde.',
        'Support-Tickets und Kommentare, einschließlich angehängter Screenshots.',
        'Nutzungsdaten: wann Sie sich zuletzt angemeldet haben und zuletzt aktiv waren, sowie ein Änderungsverlauf (wer was wann geändert hat).',
        'Nachrichten: die E-Mails und Benachrichtigungen, die die Plattform Ihnen sendet, und ob sie zugestellt wurden.',
        'Leads aus Werbeformularen, wenn das Unternehmen diese mit der Plattform verbindet.',
      ],
    },
    {
      title: '3. Warum wir sie verarbeiten',
      items: [
        'Um den Dienst zu erbringen, für den Sie oder Ihr Unternehmen sich angemeldet haben: Bestellungen annehmen und fertigen, liefern, abrechnen und unterstützen (Vertragserfüllung).',
        'Um gesetzliche Pflichten zu erfüllen, etwa Rechnungen so lange aufzubewahren, wie das Steuerrecht es verlangt.',
        'Um die Plattform sicher und zuverlässig zu halten, zum Beispiel indem festgehalten wird, wer was geändert und wer eine Lieferung quittiert hat (berechtigtes Interesse).',
      ],
      paragraphs: ['Wir verkaufen keine personenbezogenen Daten, nutzen sie nicht für Werbung und erstellen keine Nutzerprofile.'],
    },
    {
      title: '4. Cookies und Browserspeicher',
      paragraphs: [
        'Die Plattform setzt ein einziges Cookie, sm_refresh, das Sie angemeldet hält. Es ist unbedingt erforderlich, kann nicht von Skripten gelesen werden und läuft 30 Tage nach Ihrer letzten Nutzung ab (höchstens 90 Tage). Beim Abmelden wird es gelöscht.',
        'Ihr Browser speichert außerdem Ihr Anmeldetoken, Ihre Sprache und Ihre Anzeigeeinstellungen. Es gibt keine Analyse-, Werbe- oder Tracking-Cookies.',
      ],
    },
    {
      title: '5. Dienste, die wir nutzen',
      items: [
        'Google Cloud: Hosting, Datenbank und Dateispeicher in der Europäischen Union.',
        'Der von Ihrem Unternehmen eingerichtete E-Mail-Server, um Benachrichtigungen und Dokumente zu versenden.',
        'Mollie, wenn Sie online bezahlen. Karten- und Bankdaten gehen direkt an Mollie; die Plattform speichert sie nicht.',
        'Google Kalender, nur wenn Sie ihn verbinden (siehe unten).',
        'Meta (Facebook) Lead Ads, nur wenn Ihr Unternehmen seine Werbeformulare verbindet.',
      ],
    },
    {
      title: '6. Google-Nutzerdaten',
      googleLinks: true,
      paragraphs: [
        'Wenn Sie ein Google-Konto verbinden, liest die Plattform Ihr Basisprofil (Name und E-Mail-Adresse) und mit Ihrer ausdrücklichen Zustimmung Ihre Google-Kalender-Termine, um Termine wie Lieferungen anzuzeigen und zu planen. Diese Daten werden nur für diese Funktion verwendet und niemals verkauft oder für Werbung genutzt.',
        'Unsere Nutzung von Informationen aus Google-APIs entspricht der Google API Services User Data Policy einschließlich der Anforderungen zur „Limited Use“. Sie können den Zugriff jederzeit in Ihrem Google-Konto widerrufen.',
      ],
    },
    {
      title: '7. Wie lange wir sie aufbewahren',
      paragraphs: [
        'Daten werden so lange aufbewahrt, wie Ihr Konto oder Ihre Geschäftsbeziehung es erfordert. Rechnungen und die zugehörigen Daten werden so lange aufbewahrt, wie das Steuerrecht es verlangt (in den Niederlanden sieben Jahre). Anmeldesitzungen laufen wie oben beschrieben ab. Wenn ein Unternehmen die Plattform nicht mehr nutzt, werden seine Daten nach der vereinbarten Frist gelöscht.',
      ],
    },
    {
      title: '8. Sicherheit',
      paragraphs: [
        'Alle Verbindungen sind verschlüsselt (HTTPS). Der Zugriff ist nach Rollen beschränkt, sodass jeder Nutzer nur sieht, was seine Rolle erlaubt, und Änderungen werden protokolliert. Anmeldesitzungen werden nur als Einweg-Hash gespeichert und enden, wenn Sie sich abmelden oder Ihr Passwort ändern.',
      ],
    },
    {
      title: '9. Ihre Rechte',
      paragraphs: [
        'Sie haben das Recht auf Auskunft, Berichtigung und Löschung Ihrer personenbezogenen Daten, auf Einschränkung der Verarbeitung, auf Widerspruch und auf Datenübertragbarkeit. Außerdem können Sie sich bei Ihrer Datenschutzaufsichtsbehörde beschweren.',
      ],
    },
    {
      title: '10. Änderungen dieser Erklärung',
      paragraphs: [
        'Wir können diese Erklärung aktualisieren, wenn sich die Plattform ändert. Das Datum oben zeigt die aktuelle Fassung.',
      ],
    },
  ],
  googlePolicyLink: 'Google API Services User Data Policy',
  googlePermissionsLink: 'Zugriff auf Ihr Google-Konto verwalten',
  contactTitle: '11. Kontakt',
  contactCompany: 'Unternehmen',
  contactEmail: 'E-Mail',
  contactWebsite: 'Website',
  back: 'Zurück',
}

const fr: PrivacyContent = {
  title: 'Politique de confidentialité',
  updated: 'Dernière mise à jour : 22 septembre 2026',
  intro:
    'MielFlow est une plateforme que les entreprises utilisent pour gérer leurs commandes, leur production, leurs stocks, leurs devis et leurs factures. Cette politique explique quelles données personnelles la plateforme traite, pourquoi, et quels sont vos droits.',
  sections: [
    {
      title: '1. Qui est responsable',
      paragraphs: [
        'L\'entreprise qui vous a donné votre compte (votre fournisseur ou votre employeur) décide quelles commandes, quels clients et quels documents sont enregistrés, et elle est responsable de ces données. MielFlow exploite la plateforme pour son compte et ne traite les données que pour fournir le service.',
        'Pour toute question sur les données qu\'une entreprise conserve à votre sujet, adressez-vous de préférence à cette entreprise. Vous pouvez aussi contacter MielFlow à tout moment, avec les coordonnées indiquées en bas de cette page.',
      ],
    },
    {
      title: '2. Les données que nous traitons',
      items: [
        'Données de compte : nom, adresse e-mail, numéro de téléphone, langue, rôle et, pour les revendeurs, société, adresse et données financières (numéro de TVA, numéro d\'immatriculation, IBAN).',
        'Données commerciales : commandes, devis, factures, paiements, clients et les personnes qui y figurent.',
        'Données de remise : photos des marchandises livrées, signature du client, ainsi que l\'heure, l\'adresse IP et le navigateur de l\'appareil utilisé pour signer.',
        'Tickets d\'assistance et commentaires, y compris les captures d\'écran jointes.',
        'Données d\'utilisation : votre dernière connexion et votre dernière activité, et un historique des modifications (qui a modifié quoi, et quand).',
        'Messages : les e-mails et notifications que la plateforme vous envoie, et s\'ils ont été délivrés.',
        'Prospects issus de formulaires publicitaires, si l\'entreprise les relie à la plateforme.',
      ],
    },
    {
      title: '3. Pourquoi nous les traitons',
      items: [
        'Pour fournir le service auquel vous ou votre entreprise avez souscrit : prendre et fabriquer les commandes, livrer, facturer et assister (exécution d\'un contrat).',
        'Pour respecter nos obligations légales, par exemple conserver les factures aussi longtemps que la loi fiscale l\'exige.',
        'Pour garder la plateforme sûre et fiable, par exemple en enregistrant qui a modifié quoi et qui a signé une livraison (intérêt légitime).',
      ],
      paragraphs: ['Nous ne vendons pas de données personnelles, ne les utilisons pas à des fins publicitaires et ne créons pas de profils d\'utilisateurs.'],
    },
    {
      title: '4. Cookies et stockage du navigateur',
      paragraphs: [
        'La plateforme dépose un seul cookie, sm_refresh, qui vous garde connecté. Il est strictement nécessaire, ne peut pas être lu par des scripts et expire 30 jours après votre dernière utilisation (90 jours au maximum). La déconnexion le supprime.',
        'Votre navigateur conserve aussi votre jeton de connexion, votre langue et vos préférences d\'affichage. Il n\'y a aucun cookie d\'analyse, de publicité ou de suivi.',
      ],
    },
    {
      title: '5. Les services que nous utilisons',
      items: [
        'Google Cloud : hébergement, base de données et stockage de fichiers, dans l\'Union européenne.',
        'Le serveur e-mail configuré par votre entreprise, pour envoyer notifications et documents.',
        'Mollie, lorsque vous payez en ligne. Les données de carte et bancaires vont directement à Mollie ; la plateforme ne les conserve pas.',
        'Google Agenda, uniquement si vous le connectez (voir ci-dessous).',
        'Meta (Facebook) Lead Ads, uniquement si votre entreprise connecte ses formulaires publicitaires.',
      ],
    },
    {
      title: '6. Données utilisateur Google',
      googleLinks: true,
      paragraphs: [
        'Si vous connectez un compte Google, la plateforme lit votre profil de base (nom et adresse e-mail) et, avec votre autorisation explicite, vos événements Google Agenda, afin d\'afficher et de planifier des rendez-vous comme les livraisons. Ces données ne servent qu\'à cette fonction et ne sont jamais vendues ni utilisées à des fins publicitaires.',
        'Notre utilisation des informations reçues des API Google respecte la Google API Services User Data Policy, y compris les exigences de Limited Use. Vous pouvez révoquer l\'accès à tout moment dans votre compte Google.',
      ],
    },
    {
      title: '7. Durée de conservation',
      paragraphs: [
        'Les données sont conservées aussi longtemps que votre compte ou votre relation commerciale l\'exige. Les factures et les données associées sont conservées aussi longtemps que la loi fiscale l\'exige (sept ans aux Pays-Bas). Les sessions de connexion expirent comme décrit plus haut. Lorsqu\'une entreprise cesse d\'utiliser la plateforme, ses données sont supprimées après le délai convenu.',
      ],
    },
    {
      title: '8. Sécurité',
      paragraphs: [
        'Toutes les connexions sont chiffrées (HTTPS). L\'accès est limité par rôle, chaque utilisateur ne voit que ce que son rôle permet, et les modifications sont enregistrées. Les sessions de connexion ne sont conservées que sous forme de hachage à sens unique et prennent fin lorsque vous vous déconnectez ou changez de mot de passe.',
      ],
    },
    {
      title: '9. Vos droits',
      paragraphs: [
        'Vous avez le droit d\'accéder à vos données personnelles, de les rectifier ou de les effacer, d\'en limiter le traitement ou de vous y opposer, et de les recevoir dans un format portable. Vous pouvez aussi introduire une réclamation auprès de votre autorité de protection des données.',
      ],
    },
    {
      title: '10. Modifications de cette politique',
      paragraphs: [
        'Nous pouvons mettre à jour cette politique lorsque la plateforme évolue. La date en haut de la page indique la dernière version.',
      ],
    },
  ],
  googlePolicyLink: 'Google API Services User Data Policy',
  googlePermissionsLink: 'Gérer l\'accès à votre compte Google',
  contactTitle: '11. Contact',
  contactCompany: 'Société',
  contactEmail: 'E-mail',
  contactWebsite: 'Site web',
  back: 'Retour',
}

const tr: PrivacyContent = {
  title: 'Gizlilik politikası',
  updated: 'Son güncelleme: 22 Eylül 2026',
  intro:
    'MielFlow, işletmelerin siparişlerini, üretimlerini, stoklarını, tekliflerini ve faturalarını yönettiği bir platformdur. Bu politika, platformun hangi kişisel verileri neden işlediğini ve bu konuda neler yapabileceğinizi açıklar.',
  sections: [
    {
      title: '1. Sorumlu kim',
      paragraphs: [
        'Size hesabınızı veren işletme (tedarikçiniz veya işvereniniz), hangi siparişlerin, müşterilerin ve belgelerin kaydedileceğine karar verir ve bu verilerden sorumludur. MielFlow platformu o işletme adına işletir ve verileri yalnızca hizmeti sunmak için işler.',
        'Bir işletmenin sizinle ilgili sakladığı verilerle ilgili soruları en iyi o işletmeye iletebilirsiniz. MielFlow ile de her zaman bu sayfanın sonundaki bilgilerden iletişime geçebilirsiniz.',
      ],
    },
    {
      title: '2. Hangi verileri işliyoruz',
      items: [
        'Hesap bilgileri: ad, e-posta adresi, telefon numarası, dil, rol ve bayiler için firma, adres ve mali bilgiler (vergi ve ticaret sicil numaraları, IBAN).',
        'İş kayıtları: siparişler, teklifler, faturalar, ödemeler, müşteriler ve bunlarda adı geçen kişiler.',
        'Teslim kayıtları: teslim edilen ürünlerin fotoğrafları, müşterinin imzası ve imza atılan cihazın zamanı, IP adresi ve tarayıcısı.',
        'Destek talepleri ve yorumlar, eklediğiniz ekran görüntüleri dahil.',
        'Kullanım verileri: en son ne zaman giriş yaptığınız ve aktif olduğunuz, ve bir değişiklik geçmişi (kim neyi ne zaman değiştirdi).',
        'Mesajlar: platformun size gönderdiği e-postalar ve bildirimler, ve teslim edilip edilmedikleri.',
        'İşletme platforma bağlarsa, reklam formlarından gelen potansiyel müşteriler.',
      ],
    },
    {
      title: '3. Neden işliyoruz',
      items: [
        'Sizin veya işletmenizin kaydolduğu hizmeti sunmak için: sipariş almak ve üretmek, teslim etmek, faturalamak ve destek vermek (sözleşmenin ifası).',
        'Yasal yükümlülükleri yerine getirmek için, örneğin faturaları vergi mevzuatının gerektirdiği süre boyunca saklamak.',
        'Platformu güvenli ve güvenilir tutmak için, örneğin kimin neyi değiştirdiğini ve bir teslimatı kimin imzaladığını kaydederek (meşru menfaat).',
      ],
      paragraphs: ['Kişisel verileri satmıyor, reklam için kullanmıyor ve kullanıcı profili oluşturmuyoruz.'],
    },
    {
      title: '4. Çerezler ve tarayıcı depolaması',
      paragraphs: [
        'Platform, oturumunuzu açık tutan tek bir çerez kullanır: sm_refresh. Kesinlikle gereklidir, betikler tarafından okunamaz ve platformu son kullanımınızdan 30 gün sonra (en fazla 90 gün) sona erer. Çıkış yapınca silinir.',
        'Tarayıcınız ayrıca giriş anahtarınızı, dilinizi ve görünüm tercihlerinizi saklar. Analiz, reklam veya izleme çerezi yoktur.',
      ],
    },
    {
      title: '5. Kullandığımız hizmetler',
      items: [
        'Google Cloud: barındırma, veritabanı ve dosya depolama, Avrupa Birliği içinde.',
        'E-postalar ve belgeler için işletmenizin ayarladığı e-posta sunucusu.',
        'Çevrimiçi ödeme yaptığınızda Mollie. Kart ve banka bilgileri doğrudan Mollie\'ye gider; platform bunları saklamaz.',
        'Google Takvim, yalnızca siz bağlarsanız (aşağıya bakın).',
        'Meta (Facebook) Lead Ads, yalnızca işletmeniz reklam formlarını bağlarsa.',
      ],
    },
    {
      title: '6. Google kullanıcı verileri',
      googleLinks: true,
      paragraphs: [
        'Bir Google hesabı bağlarsanız platform temel profilinizi (ad ve e-posta adresi) ve açık izninizle Google Takvim etkinliklerinizi, teslimat gibi randevuları göstermek ve planlamak için okur. Bu veriler yalnızca bu özellik için kullanılır, asla satılmaz veya reklam için kullanılmaz.',
        'Google API\'lerinden alınan bilgileri kullanımımız, Limited Use gereksinimleri dahil Google API Services User Data Policy\'ye uygundur. Erişimi Google hesabınızdan istediğiniz zaman kaldırabilirsiniz.',
      ],
    },
    {
      title: '7. Ne kadar süre saklıyoruz',
      paragraphs: [
        'Veriler, hesabınız veya iş ilişkiniz gerektirdiği sürece saklanır. Faturalar ve bunların dayandığı kayıtlar vergi mevzuatının gerektirdiği süre boyunca saklanır (Hollanda\'da yedi yıl). Oturumlar yukarıda anlatıldığı gibi sona erer. Bir işletme platformu kullanmayı bıraktığında verileri kararlaştırılan sürenin sonunda silinir.',
      ],
    },
    {
      title: '8. Güvenlik',
      paragraphs: [
        'Tüm bağlantılar şifrelidir (HTTPS). Erişim role göre sınırlıdır; her kullanıcı yalnızca rolünün izin verdiğini görür ve değişiklikler kaydedilir. Oturumlar yalnızca tek yönlü özet (hash) olarak saklanır ve çıkış yaptığınızda veya şifreniz değiştiğinde sona erer.',
      ],
    },
    {
      title: '9. Haklarınız',
      paragraphs: [
        'Kişisel verilerinize erişme, bunları düzeltme veya sildirme, işlenmesini kısıtlama veya itiraz etme ve taşınabilir bir biçimde alma hakkına sahipsiniz. Ayrıca veri koruma otoritenize şikâyette bulunabilirsiniz.',
      ],
    },
    {
      title: '10. Bu politikadaki değişiklikler',
      paragraphs: [
        'Platform değiştikçe bu politikayı güncelleyebiliriz. Üstteki tarih en güncel sürümü gösterir.',
      ],
    },
  ],
  googlePolicyLink: 'Google API Services User Data Policy',
  googlePermissionsLink: 'Google hesabı erişimini yönet',
  contactTitle: '11. İletişim',
  contactCompany: 'Şirket',
  contactEmail: 'E-posta',
  contactWebsite: 'Web sitesi',
  back: 'Geri',
}

const CONTENT: Record<string, PrivacyContent> = { en, nl, de, fr, tr }

/** The policy in the UI language; anything the page does not carry reads English. */
export const privacyContentFor = (locale?: string | null): PrivacyContent =>
  CONTENT[(locale || '').slice(0, 2).toLowerCase()] ?? en

export const PRIVACY_LANGUAGES = Object.keys(CONTENT)
